import cx from 'classnames';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CheckCircle } from 'assets/icons/CircleCheck.svg';
import { ReactComponent as Loader } from 'assets/icons/Loader.svg';
import { ReactComponent as Refresh } from 'assets/icons/Refresh.svg';
import AliasAvailable from 'modules/CreateNewRoomModal/CreateRoomStepModal/AliasField/AliasAvailable';
import AliasInput from 'modules/CreateNewRoomModal/CreateRoomStepModal/AliasField/AliasInput';
import AliasNotAvailable from 'modules/CreateNewRoomModal/CreateRoomStepModal/AliasField/AliasNotAvailable';
import { slugifyExtended } from 'modules/CreateNewRoomModal/CreateRoomStepModal/AliasField/utils';

import { ScheduleMeetingFormValues } from './types';
import { SlugAvailabilityStatus } from './useSlugAvailability';

interface ScheduleMeetingAliasFieldProps {
  slugAvailability: SlugAvailabilityStatus;
  hideIcon?: boolean;
  hideHint?: boolean;
}

const ScheduleMeetingAliasField = ({ slugAvailability, hideIcon, hideHint }: ScheduleMeetingAliasFieldProps) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<ScheduleMeetingFormValues>();

  const slugPostfix = watch('slugPostfix');
  const slugValue = watch('slug');
  const name = watch('name');

  const isMatching = slugValue === slugifyExtended(name);

  const AliasIcon =
    slugAvailability === 'CHECKING'
      ? Loader
      : !isMatching && name
      ? Refresh
      : slugAvailability === 'AVAILABLE' && slugValue
      ? CheckCircle
      : undefined;

  const iconClassName = cx(
    'h-4 w-4',
    slugAvailability === 'CHECKING' && 'animate-spin stroke-gray-50',
    slugAvailability === 'AVAILABLE' && isMatching && 'hidden'
  );

  const aliasDisplayValue = `${slugValue || ''}-${slugPostfix}`;

  const AliasAvailabilityComponent =
    slugAvailability === 'AVAILABLE' && slugValue ? (
      <AliasAvailable alias={aliasDisplayValue} />
    ) : slugAvailability === 'UNAVAILABLE' ? (
      <AliasNotAvailable alias={aliasDisplayValue} />
    ) : null;

  const handleAliasIconClick = () => {
    if (!isMatching && name) {
      setValue('slug', slugifyExtended(name), { shouldDirty: true, shouldValidate: true });
      setValue('hasSlugChanged', false);
      clearErrors('slug');
    }
  };

  const placeholder =
    slugifyExtended(name) || t('schedule_meeting.name_placeholder').toLowerCase().split(' ').join('-');

  return (
    <Controller
      name="slug"
      control={control}
      render={({ field: { onChange, value } }) => (
        <AliasInput
          value={value?.toLowerCase() ?? ''}
          postfix={slugPostfix}
          placeholder={placeholder}
          onChange={(e) => {
            setValue('hasSlugChanged', true);
            onChange(e.target.value.toLowerCase());
          }}
          hasError={slugAvailability === 'UNAVAILABLE'}
          errorMessage={
            slugAvailability === 'UNAVAILABLE'
              ? t(errors.slug?.message ?? 'schedule_meeting.alias_taken_error')
              : errors.slug?.message
              ? t(errors.slug.message)
              : undefined
          }
          AliasIcon={AliasIcon}
          onAliasIconClick={handleAliasIconClick}
          iconClassName={iconClassName}
          isAliasCheckLoading={slugAvailability === 'CHECKING'}
          AliasAvailabilityComponent={AliasAvailabilityComponent}
          hintText={hideHint ? undefined : t('schedule_meeting.link_hint')}
          hideIcon={hideIcon}
        />
      )}
    />
  );
};

export default ScheduleMeetingAliasField;
